import { Box, IconButton, Stack } from '@mui/material'
import { useRef } from 'react'
import { CloseSmallIcon, HomeIcon, LogoOperations } from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { useTranslation } from '@/hooks/useTranslation'
import MenuAccordion, {
  MenuAccordionItem,
} from '@/components/common/MenuUI/MenuAccordion'
import {
  getMenuDataConfig,
  getMenuMain,
  getMenuOther,
  getMenuSensors,
  getMenuStationConfig,
} from '@/components/common/MenuUI/models'
import { MenuProvider } from '@/components/common/MenuUI/MenuContext'
import SelectLabelDashboard from '@/features/operations/components/pages/Dashboard/SelectLabelDashboard'

interface LeftSidebarContentProps {
  onClose?: () => void
}

const LeftSidebarContent = ({ onClose }: LeftSidebarContentProps) => {
  const { t } = useTranslation()
  const { interfaceId } = useAuthContext()

  const sidebarRef = useRef<HTMLDivElement | null>(null)

  const handleDashboardMenuClose = () => {
    if (!onClose) return

    // Menu is rendered in a portal, so the drawer can miss mouseleave in some cases.
    // Re-check real hover state after menu closes and close drawer if pointer is outside.
    requestAnimationFrame(() => {
      if (sidebarRef.current && !sidebarRef.current.matches(':hover')) {
        setTimeout(() => {
          onClose()
        }, 500)
      }
    })
  }

  return (
    <MenuProvider>
      <Box
        ref={sidebarRef}
        sx={{
          color: '#8A8A8A',
          width: '280px',
          height: '100%',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        <Box
          sx={{
            padding: '20px',
            borderBottom: '1px solid rgba(255,255,255,0.3)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="15px"
            mb="15px"
          >
            <LogoOperations
              fill="white"
              sx={{ width: '94px', height: '17px' }}
            />
          </Stack>
          <Stack>
            <SelectLabelDashboard
              sx={{
                width: '240px',
                '& .select-label__dashboard, & .select-label__wrap__item': {
                  width: '240px',
                },
                '& .select-label__dashboard': {
                  padding: '9px 12px',
                },
                '& .select-label__dashboard p': {
                  fontSize: '12px',
                  fontWeight: 400,
                },
              }}
              colorIcon="#66f0a0"
              width="240px"
              onClose={handleDashboardMenuClose}
              menuProps={{
                id: 'SelectLabelDashboard-GlobalHoverSidebar',
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ padding: '20px' }}>
          <MenuAccordionItem
            icon={<HomeIcon />}
            text={t('menuDashboard')}
            to={'/dashboard'}
            sx={{ marginBottom: '20px' }}
            closeMenu={() => {}}
          />

          <MenuAccordion
            id="hover-menu-operations"
            title={t('menuOperationsTitle')}
            alwaysExpanded
            sx={{
              '& .MuiAccordionSummary-root': { paddingTop: 0 },
            }}
          >
            {getMenuMain(t, Number(interfaceId)).map((i) => (
              <MenuAccordionItem
                disabled={i.disabled}
                key={i.label}
                icon={i.icon}
                text={i.label}
                to={i.link || ''}
                closeMenu={() => {}}
              />
            ))}
          </MenuAccordion>

          <MenuAccordion
            id="hover-menu-sensors"
            title={t('menuSensorsTitle')}
            alwaysExpanded
          >
            {getMenuSensors(t, Number(interfaceId)).map((i) => (
              <MenuAccordionItem
                disabled={i.disabled}
                key={i.label}
                icon={i.icon}
                text={i.label}
                to={i.link || ''}
                closeMenu={() => {}}
              />
            ))}
          </MenuAccordion>

          <MenuAccordion
            id="hover-menu-station-config"
            title={t('menuStationConfigTitle')}
            alwaysExpanded
          >
            {getMenuStationConfig(t).map((i) => (
              <MenuAccordionItem
                disabled={i.disabled}
                key={i.label}
                icon={i.icon}
                text={i.label}
                to={i.link || ''}
                closeMenu={() => {}}
              />
            ))}
          </MenuAccordion>

          <MenuAccordion
            id="hover-menu-data-config"
            title={t('menuDataConfigTitle')}
            alwaysExpanded
          >
            {getMenuDataConfig(t).map((i) => (
              <MenuAccordionItem
                disabled={i.disabled}
                key={i.label}
                icon={i.icon}
                text={i.label}
                to={i.link || ''}
                closeMenu={() => {}}
              />
            ))}
          </MenuAccordion>

          <MenuAccordion
            id="hover-menu-other"
            title={t('menuOtherTitle')}
            alwaysExpanded
          >
            {getMenuOther(t).map((i) => (
              <MenuAccordionItem
                disabled={i.disabled}
                key={i.label}
                icon={i.icon}
                text={i.label}
                to={i.link || ''}
                closeMenu={() => {}}
              />
            ))}
          </MenuAccordion>
        </Box>
      </Box>
    </MenuProvider>
  )
}

export default LeftSidebarContent
